import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Paper,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Map as FloorPlanIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Straighten as MeasureIcon,
  Room as RoomIcon,
  SquareFoot as SqftIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Fullscreen as FullscreenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`floorplan-tabpanel-${index}`}
      aria-labelledby={`floorplan-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function FloorPlanManager({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [floorPlans, setFloorPlans] = useState([]);

  // Mock floor plan data
  const mockFloorPlans = [
    {
      id: 1,
      title: 'Main Floor Plan',
      description: 'Detailed floor plan of the main level',
      type: 'Main Floor',
      url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      thumbnail: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400',
      uploadedAt: new Date('2024-07-15'),
      size: '2.1 MB',
      format: 'PDF',
      rooms: [
        { name: 'Living Room', area: 250, dimensions: '15\' x 16\'8"' },
        { name: 'Kitchen', area: 180, dimensions: '12\' x 15\'' },
        { name: 'Dining Room', area: 120, dimensions: '10\' x 12\'' },
        { name: 'Master Bedroom', area: 200, dimensions: '14\' x 14\'3"' },
        { name: 'Bedroom 2', area: 140, dimensions: '11\' x 12\'8"' },
        { name: 'Bathroom 1', area: 60, dimensions: '8\' x 7\'6"' },
        { name: 'Bathroom 2', area: 45, dimensions: '6\' x 7\'6"' },
      ],
      totalArea: 995,
      interactive: true,
    },
    {
      id: 2,
      title: 'Second Floor Plan',
      description: 'Upper level floor plan',
      type: 'Second Floor',
      url: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800',
      thumbnail: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=400',
      uploadedAt: new Date('2024-07-14'),
      size: '1.8 MB',
      format: 'PDF',
      rooms: [
        { name: 'Bedroom 3', area: 160, dimensions: '12\' x 13\'4"' },
        { name: 'Bedroom 4', area: 140, dimensions: '11\' x 12\'8"' },
        { name: 'Bathroom 3', area: 50, dimensions: '7\' x 7\'2"' },
        { name: 'Loft Area', area: 200, dimensions: '16\' x 12\'6"' },
      ],
      totalArea: 550,
      interactive: false,
    },
  ];

  useEffect(() => {
    if (property) {
      setFloorPlans(mockFloorPlans);
    }
  }, [property]);

  const onDrop = useCallback((acceptedFiles) => {
    setUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploading(false);
          setUploadProgress(0);

          const newFloorPlans = acceptedFiles.map((file, index) => ({
            id: Date.now() + index,
            title: file.name.replace(/\.[^/.]+$/, ''),
            description: '',
            type: 'Main Floor',
            url: URL.createObjectURL(file),
            thumbnail: URL.createObjectURL(file),
            uploadedAt: new Date(),
            size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
            format: file.name.split('.').pop().toUpperCase(),
            rooms: [],
            totalArea: 0,
            interactive: false,
          }));

          setFloorPlans((prev) => [...prev, ...newFloorPlans]);
          return 0;
        }
        return prev + 10;
      });
    }, 200);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddFloorPlan = () => {
    setSelectedFloorPlan(null);
    setOpenDialog(true);
  };

  const handleEditFloorPlan = (floorPlan) => {
    setSelectedFloorPlan(floorPlan);
    setOpenDialog(true);
  };

  const handleDeleteFloorPlan = (floorPlanId) => {
    setFloorPlans((prev) => prev.filter((fp) => fp.id !== floorPlanId));
  };

  const handleSaveFloorPlan = (floorPlanData) => {
    if (selectedFloorPlan) {
      setFloorPlans((prev) => prev.map((fp) => (fp.id === selectedFloorPlan.id ? { ...fp, ...floorPlanData } : fp)));
    } else {
      const newFloorPlan = {
        id: Date.now(),
        ...floorPlanData,
        uploadedAt: new Date(),
        rooms: [],
        totalArea: 0,
      };
      setFloorPlans((prev) => [...prev, newFloorPlan]);
    }
    setOpenDialog(false);
  };

  const renderFloorPlanGallery = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">
          Floor Plans (
          {floorPlans.length}
          {' '}
          plans)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddFloorPlan}
        >
          Add Floor Plan
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Uploading...
            {' '}
            {uploadProgress}
            %
          </Typography>
        </Box>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          bgcolor: isDragActive ? 'primary.50' : 'background.paper',
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop files here' : 'Drag & drop floor plans here'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supports PDF, JPG, PNG files
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {floorPlans.map((floorPlan) => (
          <Grid item xs={12} sm={6} md={4} key={floorPlan.id}>
            <Card>
              <Box sx={{ position: 'relative' }}>
                <img
                  src={floorPlan.thumbnail}
                  alt={floorPlan.title}
                  style={{ width: '100%', height: 200, objectFit: 'cover' }}
                />
                <Chip
                  label={floorPlan.type}
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', top: 8, left: 8 }}
                />
                {floorPlan.interactive && (
                  <Chip
                    label="Interactive"
                    color="success"
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  />
                )}
              </Box>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {floorPlan.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {floorPlan.description}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {floorPlan.totalArea}
                    {' '}
                    sqft
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {floorPlan.format}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => setSelectedFloorPlan(floorPlan)}
                    fullWidth
                  >
                    View Plan
                  </Button>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEditFloorPlan(floorPlan)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteFloorPlan(floorPlan.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderRoomDetails = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Room Details
      </Typography>
      {floorPlans.map((floorPlan) => (
        <Accordion key={floorPlan.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{floorPlan.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    <TableCell>Area (sqft)</TableCell>
                    <TableCell>Dimensions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {floorPlan.rooms.map((room, index) => (
                    <TableRow key={index}>
                      <TableCell>{room.name}</TableCell>
                      <TableCell>{room.area}</TableCell>
                      <TableCell>{room.dimensions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  const renderInteractiveTools = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Interactive Tools
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Measurement Tools
              </Typography>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <MeasureIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Distance Measurement"
                    secondary="Measure distances between points"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <SqftIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Area Calculation"
                    secondary="Calculate room and total areas"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Navigation Tools
              </Typography>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <ZoomInIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Zoom In/Out"
                    secondary="Zoom to see details or overview"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <RotateIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Rotate View"
                    secondary="Rotate the floor plan view"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Floor Plan Management
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Floor Plans" icon={<FloorPlanIcon />} />
        <Tab label="Room Details" icon={<RoomIcon />} />
        <Tab label="Interactive Tools" icon={<MeasureIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {renderFloorPlanGallery()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderRoomDetails()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderInteractiveTools()}
      </TabPanel>

      {/* Add/Edit Floor Plan Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFloorPlan ? 'Edit Floor Plan' : 'Add New Floor Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Title *"
                value={selectedFloorPlan?.title || ''}
                onChange={(e) => setSelectedFloorPlan((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={selectedFloorPlan?.description || ''}
                onChange={(e) => setSelectedFloorPlan((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={selectedFloorPlan?.type || ''}
                  onChange={(e) => setSelectedFloorPlan((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))}
                  label="Type *"
                >
                  <MenuItem value="Main Floor">Main Floor</MenuItem>
                  <MenuItem value="Second Floor">Second Floor</MenuItem>
                  <MenuItem value="Basement">Basement</MenuItem>
                  <MenuItem value="Attic">Attic</MenuItem>
                  <MenuItem value="Site Plan">Site Plan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Area (sqft)"
                value={selectedFloorPlan?.totalArea || ''}
                onChange={(e) => setSelectedFloorPlan((prev) => ({
                  ...prev,
                  totalArea: e.target.value,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Floor Plan URL *"
                value={selectedFloorPlan?.url || ''}
                onChange={(e) => setSelectedFloorPlan((prev) => ({
                  ...prev,
                  url: e.target.value,
                }))}
                fullWidth
                placeholder="https://example.com/floorplan.pdf"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Thumbnail URL"
                value={selectedFloorPlan?.thumbnail || ''}
                onChange={(e) => setSelectedFloorPlan((prev) => ({
                  ...prev,
                  thumbnail: e.target.value,
                }))}
                fullWidth
                placeholder="https://example.com/thumbnail.jpg"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={selectedFloorPlan?.interactive || false}
                    onChange={(e) => setSelectedFloorPlan((prev) => ({
                      ...prev,
                      interactive: e.target.checked,
                    }))}
                  />
                )}
                label="Interactive Floor Plan"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSaveFloorPlan(selectedFloorPlan)}
            variant="contained"
            disabled={!selectedFloorPlan?.title || !selectedFloorPlan?.url || !selectedFloorPlan?.type}
          >
            {selectedFloorPlan?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen View Dialog */}
      <Dialog
        open={!!selectedFloorPlan && !openDialog}
        onClose={() => setSelectedFloorPlan(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedFloorPlan?.title}
          <IconButton
            onClick={() => setSelectedFloorPlan(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedFloorPlan && (
            <Box>
              <img
                src={selectedFloorPlan.url}
                alt={selectedFloorPlan.title}
                style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">{selectedFloorPlan.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFloorPlan.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                  >
                    Download
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ShareIcon />}
                  >
                    Share
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default FloorPlanManager;
